/**
 * Thực hiện yêu cầu xác thực kênh riêng tới endpoint broadcasting của API.
 */
async function doAuthorize(channel, options, socketId, callback, getToken, defaultEndpoint, fetchImpl) {
  const token = typeof getToken === 'function' ? getToken() : getToken
  const endpoint = options?.authEndpoint || defaultEndpoint || '/_vims_api/broadcasting/auth'
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null)

  if (!doFetch) {
    callback(new Error('Fetch API không khả dụng'), null)

    return
  }

  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options?.auth?.headers || {}),
  }

  try {
    const res = await doFetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        socket_id: socketId,
        channel_name: channel?.name,
      }),
    })

    if (!res.ok) {
      const errText = typeof res.text === 'function' ? await res.text().catch(() => '') : ''

      callback(new Error(`WebSocket auth thất bại (${res.status}): ${errText}`), null)

      return
    }

    const data = typeof res.json === 'function' ? await res.json() : res

    callback(null, data)
  } catch (err) {
    callback(err, null)
  }
}

/**
 * Trình xác thực kênh riêng Echo / Pusher (đặc tả kênh websocket riêng L52).
 * Lấy Bearer token động qua hàm getToken để khi người dùng đăng nhập lại,
 * kênh được cấp quyền bằng token mới nhất thay vì chụp một lần lúc khởi tạo.
 */
export function createEchoAuthorizer(getToken, defaultEndpoint, fetchImpl) {
  return (channel, options) => ({
    authorize: (socketId, callback) => {
      doAuthorize(channel, options, socketId, callback, getToken, defaultEndpoint, fetchImpl)
    },
  })
}

/**
 * Dựng cấu hình khởi tạo Laravel Echo với Pusher và cơ chế xác thực kênh riêng.
 */
export function buildEchoConfig(config = {}, getToken = null, fetchImpl = null) {
  const socketHost = config.socketHost
  const socketPort = config.socketPort
  const socketKey = config.socketKey
  const apiBaseUrl = config.apiBaseUrl || '/_vims_api'
  const authEndpoint = `${apiBaseUrl}/broadcasting/auth`

  return {
    broadcaster: 'pusher',
    key: socketKey,
    cluster: 'mt1',
    wsHost: socketHost,
    wsPort: socketPort,
    wssPort: socketPort,
    forceTLS: false,
    disableStats: true,
    enabledTransports: ['ws', 'wss'],
    authEndpoint,
    authorizer: createEchoAuthorizer(getToken, authEndpoint, fetchImpl),
  }
}

/**
 * Tìm kênh đã đăng ký trong Echo connector mà không tạo mới (NÊN 4).
 * Tránh gửi yêu cầu POST xác thực thừa khi gỡ bỏ sự kiện (removeEventListener).
 */
export function findExistingChannel(echoInstance, channelName, isPrivate = false) {
  if (!echoInstance) return null
  const channels = echoInstance.connector?.channels

  if (!channels) return null

  if (isPrivate) {
    const key = channelName.startsWith('private-') ? channelName : `private-${channelName}`

    return channels[key] || null
  }

  return channels[channelName] || null
}

/**
 * Rời toàn bộ các kênh websocket Echo đã đăng ký khi người dùng đăng xuất (NÊN 3).
 */
export function leaveAllEchoChannels(echoInstance) {
  if (!echoInstance) return
  const channels = echoInstance.connector?.channels

  if (!channels) return

  Object.keys(channels).forEach(channelKey => {
    try {
      echoInstance.leaveChannel(channelKey)
    } catch {
      // Bỏ qua lỗi nếu kênh đã rời trước đó
    }
  })
}
